/**
 * Static page content for the text-only routes (method, label guide, safety).
 *
 * Extracted from App.tsx on 2026-08-02. It used to live inline in the DetailPage component,
 * so scripts/prerender.mjs could not reach it and emitted only an H1 plus the meta description
 * for these routes. /label-guide/ was therefore serving crawlers ~80 words while carrying 232
 * impressions at position 43.7 — the largest search-demand pool on the site, effectively
 * invisible to Google. Keeping the copy here lets the prerenderer emit the real content.
 *
 * `privacy` deliberately stays in App.tsx: its Advertising section contains JSX (a link), which
 * this module cannot hold as a string and the prerenderer could not stringify. Privacy is not
 * an SEO target, so it loses nothing.
 */
export type PageSection = { title: string; text: string };
export type StaticPage = { title: string; intro: string; sections: PageSection[] };
export type StaticPageKey = "method" | "label-guide" | "safety";

export const staticPages: Record<StaticPageKey, StaticPage> = {
    method: {
      title: "Calculation Method",
      intro:
        "The calculator separates three questions: how many calories the pet may need, how many grams of this exact food provide those calories, and whether a cup estimate is useful for people using US-style household measures.",
      sections: [
        {
          title: "Daily calorie estimate",
          text:
            "The calorie estimate uses a standard veterinary energy approach based on body weight in kilograms. It is a population-level starting point and is not a body-condition assessment.",
        },
        {
          title: "Life-stage adjustments",
          text:
            "The estimate is adjusted for dog or cat and broad life-stage goals. Adult, growth, neuter status, obesity-prone, and weight-loss selections use different starting values. Weight-loss values should be treated as supervised estimates, especially for cats.",
        },
        {
          title: "Metric and cup outputs",
          text:
            "Grams per day come from daily calories divided by the food's calorie density. If the label says kcal/100g, the calculator converts it to kcal/kg internally. This is why 400 g of wet food and 400 g of dry food can produce very different calories: the calorie-density input is different.",
        },
        {
          title: "Weight trend chart",
          text:
            "For weight-loss style selections, the chart shows a guideline monitoring band rather than an exact prediction. It uses commonly cited veterinary weekly weight-loss rates: about 1-2% body weight per week for dogs and about 0.5-1% per week for cats. Maintenance and growth selections show monitoring only because this calculator does not know body condition score, target weight, activity, illness, or treats.",
        },
      ],
    },
    "label-guide": {
      // H1 was "How to Read the Food Label" — it omitted "pet", the one word present in every
      // query driving this page's 232 impressions ("pet food labeling", "pet food labels",
      // "how to read pet food labels"). Both spellings of label(l)ing draw roughly equal
      // impressions, so the intro carries both deliberately.
      title: "How to Read a Pet Food Label",
      intro:
        "Pet food labelling differs by region, and the useful numbers are not always in the same place. Metric feeding usually starts with kcal/kg, cup feeding needs kcal/cup, and US labeling puts both under a Calorie Content statement. This guide shows where to find them on dog and cat food labels in the EU, the UK, and the US.",
      sections: [
        {
          title: "EU and UK labels",
          text:
            "Look for energy content, metabolizable energy, metabolisable energy, kcal/kg, or kcal/100g. This can appear on dry, wet, or complementary products. Under EU feed marketing rules and FEDIAF labelling guidance, pet food labels focus on clear composition, intended use, analytical constituents, and responsible feeding information.",
        },
        {
          title: "US labels",
          text:
            "In the US, look for the Calorie Content statement. AAFCO label guidance commonly expresses energy as kcal/kg and also as kcal per familiar unit such as cup, can, or treat. Enter kcal/cup only if you want cup output.",
        },
        {
          title: "Life-stage wording",
          text:
            "Choose food whose intended use matches your pet: growth, adult maintenance, all life stages, or a dietetic/nutritional purpose when appropriate. FEDIAF and AAFCO both publish nutrient guidance, but neither replaces advice from your veterinarian for medical feeding plans.",
        },
      ],
    },
    safety: {
      title: "Safety and Veterinary Context",
      intro:
        "A calculator is useful for starting a conversation, but feeding plans can become medical decisions.",
      sections: [
        {
          title: "When to call your veterinarian",
          text:
            "Seek veterinary advice for pets that are underweight, obese, pregnant, lactating, diabetic, kidney-compromised, heart-compromised, recovering from illness, or younger than expected for their size. Rapid weight loss is particularly dangerous in cats.",
        },
        {
          title: "Monitor and adjust",
          text:
            "Recheck weight and body condition every two to four weeks after changing portions. Weekly trend bands are useful for spotting direction, but adjust gradually rather than making dramatic cuts, and include treats, toppers, chews, and table food in the daily calorie total.",
        },
      ],
    },};

/**
 * Internal links from each static hub page into its matching articles.
 *
 * SINGLE SOURCE OF TRUTH. Both src/App.tsx (hydrated view) and scripts/prerender.mjs (the HTML
 * crawlers actually read) import this. It was briefly duplicated in both files on 2026-08-02;
 * duplicated route maps drift silently, so it lives here instead.
 *
 * Why it exists: /label-guide/ and /articles/how-to-read-calorie-content-pet-food-label/ target
 * overlapping queries and sat unlinked, competing rather than reinforcing each other.
 */
export const relatedForPage: Record<string, string[]> = {
  "label-guide": ["how-to-read-calorie-content-pet-food-label", "reading-labels-that-matter", "food-quality-without-marketing-hype"],
  method: ["overfeeding-risks", "underfeeding-signs", "healthy-weight-and-longevity"],
  safety: ["food-related-red-flags", "why-owners-miss-overweight-pet"],
};
